import { z } from "zod";
import { userAreaSchema, userRolesSchema } from "./routineAreas.ts";

// Basic structural validation for shared types (used by both api and web).
// The ent schema provides deeper validation via Croner at write time.
const CRON_FIELD = String.raw`[\d*,/\-]+`;
const CRON_REGEX = new RegExp(`^${CRON_FIELD}(\\s+${CRON_FIELD}){4}$`);
export const cronSchedule = z
  .string()
  .refine((s) => CRON_REGEX.test(s.trim()), { message: "Invalid cron expression" });

// An amendment is the only way to change what a system routine does, so the cap
// has to leave room for a real rewrite of one section rather than a line or two.
// Kept in step with the skill amendment cap.
export const ROUTINE_AMENDMENT_MAX = 5000;

const calendarPreFilters = {
  participants: z.array(z.string()).optional(),
  organizer: z.string().optional(),
  subjectContains: z.string().optional(),
  minDurationMinutes: z.number().optional(),
  maxDurationMinutes: z.number().optional(),
};

// Header-shaped conditions shared by the two mail triggers. Both narrow the same
// way; they differ in *when* they are evaluated, not in what they can match on.
const emailPreFilters = {
  fromSenders: z.array(z.string()).optional(),
  toRecipients: z.array(z.string()).optional(),
  ccRecipients: z.array(z.string()).optional(),
  subjectContains: z.string().optional(),
};

export const triggerConfigSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("email"),
    classifierPrompt: z.string().optional(),
    ...emailPreFilters,
    // Auto-inbox classification labels (by name, e.g. "action needed") that gate
    // this trigger. Any-of: the routine fires when the source carries at least
    // one listed label. Empty/absent means "no label gate". Kept in sync with
    // the ent schema's triggerConfigSchema in `ent/schema/routine.ts`.
    classificationLabels: z.array(z.string()).optional(),
    // Auto-inbox labels that make this trigger stand down, whatever else the
    // source carries. Any-of, and it wins over `classificationLabels`. Kept in
    // sync with the ent schema's triggerConfigSchema in `ent/schema/routine.ts`,
    // which carries the full rationale.
    excludeClassificationLabels: z.array(z.string()).optional(),
    // Stand down when another okthx thread is already answering this email
    // conversation. Opt-in (absent = false). Kept in sync with the ent schema's
    // triggerConfigSchema in `ent/schema/routine.ts`, which carries the full
    // rationale.
    skipWhenThreadSubscribed: z.boolean().optional(),
    // Stand down on a calendar-invitation email — an ICS-bearing invite, update
    // or cancellation, or a canned RSVP auto-reply. Opt-in (absent = false).
    // Kept in sync with the ent schema's triggerConfigSchema in
    // `ent/schema/routine.ts`, which carries the full rationale.
    excludeCalendarInvitations: z.boolean().optional(),
  }),
  // Fires when a conversation *gains* one of these auto-inbox labels. Any-of, by
  // name, same vocabulary as `email`'s `classificationLabels`.
  //
  // The difference from that filter is when the labels are read, and it is the
  // whole point of the variant. A thread's label set is settled by
  // `reconcileEmailThreadLabels` at the end of extraction, while the `email`
  // trigger's check re-loads the source whenever its job happens to run — inline
  // at source creation under `syncJobExecution`, off the bulk queue in
  // production. Those two moments see different label sets. This trigger is
  // dispatched from the reconcile itself, so it reads the set that was actually
  // stored. Kept in sync with the ent schema's triggerConfigSchema in
  // `ent/schema/routine.ts`.
  z.object({
    type: z.literal("emailLabeled"),
    labels: z.array(z.string()).min(1),
    classifierPrompt: z.string().optional(),
    ...emailPreFilters,
  }),
  z.object({
    type: z.literal("beforeMeeting"),
    minutesBefore: z.number(),
    classifierPrompt: z.string().optional(),
    ...calendarPreFilters,
  }),
  z.object({
    type: z.literal("afterMeeting"),
    minutesAfter: z.number(),
    classifierPrompt: z.string().optional(),
    ...calendarPreFilters,
  }),
  z.object({
    type: z.literal("inviteReceived"),
    classifierPrompt: z.string().optional(),
    ...calendarPreFilters,
  }),
  z.object({
    type: z.literal("inviteUpdated"),
    classifierPrompt: z.string().optional(),
    ...calendarPreFilters,
  }),
  z.object({
    type: z.literal("rsvpResponse"),
    responseStatus: z.enum(["accepted", "declined", "tentative", "any"]),
    classifierPrompt: z.string().optional(),
    ...calendarPreFilters,
  }),
  z.object({
    type: z.literal("eventCancelled"),
    classifierPrompt: z.string().optional(),
    ...calendarPreFilters,
  }),
  z.object({
    type: z.literal("cron"),
    schedule: cronSchedule,
    timezone: z
      .string()
      .refine(
        (tz) => {
          try {
            Intl.DateTimeFormat("en-US", { timeZone: tz });
            return true;
          } catch {
            return false;
          }
        },
        { message: "Invalid IANA timezone identifier" },
      )
      .optional(),
  }),
  // Fires on each new top-level message in one Slack channel the owner is a
  // member of, except okthx's own. `channelName` is display only. Kept in sync
  // with the ent schema's triggerConfigSchema in `ent/schema/routine.ts`.
  z.object({
    type: z.literal("slackMessage"),
    teamId: z.string().min(1),
    channelId: z.string().min(1),
    channelName: z.string(),
    classifierPrompt: z.string().optional(),
  }),
  // Fires on a message in a DM or group DM the owner is in, from anyone. One run
  // thread per conversation; later messages file into it. `slackUserId` is the
  // owner's Slack id, set by the server. Kept in sync with `ent/schema/routine.ts`.
  z.object({
    type: z.literal("slackDirectMessage"),
    teamId: z.string().min(1),
    slackUserId: z.string().min(1),
    classifierPrompt: z.string().optional(),
  }),
  // Fires when a message in a channel the owner is in mentions them or a user
  // group they're in. `slackUserId` is the owner's Slack id, set by the server.
  // Kept in sync with `ent/schema/routine.ts`.
  z.object({
    type: z.literal("slackMention"),
    teamId: z.string().min(1),
    slackUserId: z.string().min(1),
    classifierPrompt: z.string().optional(),
  }),
  // Fires as each newly synced inbound email is extracted. Used by "action"
  // execution-kind routines, which run inline in the extraction pipeline rather
  // than fanning out to a thread. Kept in sync with the ent schema's
  // triggerConfigSchema in `ent/schema/routine.ts`.
  z.object({
    type: z.literal("newMail"),
  }),
]);

export type TriggerConfig = z.infer<typeof triggerConfigSchema>;

/**
 * Whether the routine form offers the Slack-message trigger. `unavailable`
 * hides it; `reconnect` shows it with a prompt to reconnect Slack, because the
 * connector predates the scopes it needs.
 */
export type SlackTriggerAvailabilityResp =
  | { status: "unavailable" }
  | { status: "reconnect"; connectorId: number }
  | { status: "ready"; teamId: string; teamName?: string };

/** A channel the user can pick for a Slack-message trigger. */
export type SlackTriggerChannel = { id: string; name: string; isPrivate: boolean };

export type SlackTriggerChannelsResp = { channels: SlackTriggerChannel[] };

/**
 * Cap on a routine's user-facing description. It renders as a single clamped
 * line under the routine's name, and the agent writes it unattended — without a
 * bound it drifts into a paragraph that the list row can only truncate.
 */
export const ROUTINE_DESCRIPTION_MAX_LENGTH = 200;

export const routinePermissionsSchema = z.object({
  autoSendCommunication: z.boolean(),
  autoArchive: z.boolean(),
  defaultFrom: z.enum(["user", "assistant"]),
});
export type RoutinePermissions = z.infer<typeof routinePermissionsSchema>;

/**
 * Permissions a brand-new routine starts with, wherever it is created — the
 * agent's `routines.NEW_ROUTINE` and the web new-routine form both seed from
 * here.
 *
 * Shared rather than duplicated because the two had silently drifted on
 * `autoArchive`: the agent set it on and the form seeded it off, so the same
 * routine either closed its run threads or left every one of them open forever
 * depending on which surface built it. That is invisible at both call sites and
 * only shows up as a pile of `to-review` threads weeks later (found while
 * investigating BIG-7214).
 *
 * - `autoSendCommunication` off: a routine that mails people without the user
 *   seeing it first is something they agree to, not something they get by
 *   default.
 * - `autoArchive` on: a routine that finishes on its own has nothing to leave in
 *   the inbox. A run that leaves a draft awaiting the user does not archive —
 *   `threadHasPendingApproval` reclassifies it as `waitingForUser` first. A
 *   question answered by a prose reply is not such a run — it stops counting as
 *   pending, so the thread archives like any other clean finish (BIG-7398).
 *   A later turn that ends cleanly does not archive over a draft an earlier
 *   turn left pending either: the archive step checks every turn
 *   (`threadHasPendingCardInAnyTurn`, BIG-7279).
 */
export const NEW_ROUTINE_DEFAULT_PERMISSIONS: RoutinePermissions = {
  autoSendCommunication: false,
  autoArchive: true,
  defaultFrom: "assistant",
};

// --- System routine settings ---
//
// A system routine may declare a list of settings (toggles, select-from-list,
// free-text). The user's chosen values are interpolated into the routine's
// `prompt.md` Eta template at run time (referenced as `it.settings.<key>`), so
// `key` must be a valid JS identifier. Values are stored as a per-user
// preferences override (see `routines/preferences.ts`) layered on the shipped
// definition.
const settingKey = z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, "key must be a valid identifier");

// Semantic color tokens for a label. Mapped to provider-specific palettes
// (Gmail label colors, Outlook category presets) when labels are pushed.
export const emailLabelColorSchema = z.enum([
  "gray",
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
  "blue",
  "sky",
  "purple",
  "pink",
]);
export type EmailLabelColor = z.infer<typeof emailLabelColorSchema>;

// One entry in a `labelList` setting: a classification label the user can edit.
// The catalog is consumed in two places — the always-on email classifier (which
// sees `name` + `description` to pick a label) and the provider push (which uses
// `color` / `autoArchive` when the routine is enabled).
// Which dimension a label belongs to. Labels are multi-assignable (like Gmail):
// - "type": what the email is. Mutually exclusive — at most one per email
//   (orders / travel / newsletters / promotional / updates).
// - "signal": what it needs. Additive — zero or more (action needed / awaiting reply).
// - "sender": assigned by code from contact history, never by the classifier
//   (new sender). Excluded from the model's candidate set.
export const emailLabelGroupSchema = z.enum(["type", "signal", "sender"]);
export type EmailLabelGroup = z.infer<typeof emailLabelGroupSchema>;

export const emailLabelCatalogItemSchema = z.object({
  // Stable id so the FE can key rows and a rename doesn't reshuffle them.
  id: z.string().min(1),
  name: z.string().min(1).max(200),
  // What the classifier reads to decide membership; also the user-facing blurb.
  // Bounded: custom labels are injected into the classifier prompt, so an
  // unbounded description could bloat the prompt / degrade classification.
  description: z.string().max(500),
  color: emailLabelColorSchema,
  group: emailLabelGroupSchema.default("type"),
  // When this label is applied and the routine is enabled, also archive the
  // message in the provider mailbox.
  autoArchive: z.boolean().default(false),
  // A disabled label is still classified and stored locally, but never pushed to
  // the user's mailbox. Defaults to enabled.
  enabled: z.boolean().default(true),
});
export type EmailLabelCatalogItem = z.infer<typeof emailLabelCatalogItemSchema>;

export const routineSettingDefSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("toggle"),
      key: settingKey,
      label: z.string(),
      description: z.string().optional(),
      default: z.boolean().default(false),
    }),
    z.object({
      type: z.literal("select"),
      key: settingKey,
      label: z.string(),
      description: z.string().optional(),
      options: z.array(z.object({ value: z.string(), label: z.string() })).min(1),
      default: z.string(),
    }),
    z.object({
      type: z.literal("text"),
      key: settingKey,
      label: z.string(),
      description: z.string().optional(),
      placeholder: z.string().optional(),
      multiline: z.boolean().optional(),
      default: z.string().default(""),
    }),
    // An editable, ordered list of classification labels (see
    // emailLabelCatalogItemSchema). The value stored per user is the whole array.
    z.object({
      type: z.literal("labelList"),
      key: settingKey,
      label: z.string(),
      description: z.string().optional(),
      default: z.array(emailLabelCatalogItemSchema).default([]),
    }),
    // A pick-several over options filled at read time from the user's own data
    // (see routines/settingsBacking.ts). The stored value is the array of chosen
    // option values.
    z.object({
      type: z.literal("multiSelect"),
      key: settingKey,
      label: z.string(),
      description: z.string().optional(),
      default: z.array(z.string()).default([]),
      options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
    }),
    // A multi-select of the user's own notification channels (email, text, push,
    // …). The stored value is an array of channel keys. `options` is NOT authored
    // — the server fills it at read time with the channels the user has connected
    // (see systemRoutineToResp), so the picker only ever offers reachable channels.
    z.object({
      type: z.literal("notificationChannels"),
      key: settingKey,
      label: z.string(),
      description: z.string().optional(),
      default: z.array(z.string()).default([]),
      options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
    }),
  ])
  // A select's `default` must name one of its options — catch authoring
  // mistakes at parse time (import) rather than rendering an empty picker.
  .refine((def) => def.type !== "select" || def.options.some((o) => o.value === def.default), {
    message: "select default must be one of its declared options",
    path: ["default"],
  });
export type RoutineSettingDef = z.infer<typeof routineSettingDefSchema>;

// A user's chosen values, keyed by setting `key`. Toggles are booleans; selects
// and text are strings; a labelList is an array of catalog items; a
// multiSelect or notificationChannels value is an array of option-value strings.
export const routineSettingValuesSchema = z.record(
  z.string(),
  z.union([z.string(), z.boolean(), z.array(emailLabelCatalogItemSchema), z.array(z.string())]),
);
export type RoutineSettingValues = z.infer<typeof routineSettingValuesSchema>;

export const createRoutineReq = z.object({
  name: z.string(),
  description: z.string().max(ROUTINE_DESCRIPTION_MAX_LENGTH).optional(),
  prompt: z.string(),
  // Only meaningful when editing a system routine, where the prompt is shipped
  // text the user cannot change. Sending it is what distinguishes a current
  // client from one deployed before amendments existed.
  amendment: z.string().max(ROUTINE_AMENDMENT_MAX).optional(),
  triggerConfig: triggerConfigSchema,
  permissions: routinePermissionsSchema,
  enabled: z.boolean(),
  /**
   * Absolute instant (ISO 8601, UTC) after which the routine stops firing and
   * turns itself off. Absent means it runs until the user disables it. Optional
   * so a currently-deployed frontend that predates this field can still save —
   * `updateRoutine` merges over the stored data, so an omitted value is
   * preserved rather than cleared.
   */
  endsAt: z.string().datetime().optional(),
  /**
   * The routine's area — a built-in area id, or a name the user added — and
   * the roles it suits. Optional, so a client that predates them still saves;
   * `updateRoutine` merges over the stored data, so an omitted value is kept.
   */
  area: userAreaSchema.optional(),
  roles: userRolesSchema.optional(),
});

// The email-label catalog editor's read/write shape. Built-in labels are
// code-owned: their name/description/group are read-only and the server ignores
// any edits to them, deriving only sparse color/auto-archive overrides. Custom
// labels are fully owned by the user.
export const updateEmailLabelConfigReq = z.object({
  builtIns: z.array(emailLabelCatalogItemSchema),
  customLabels: z.array(emailLabelCatalogItemSchema),
  labelPrefix: z.boolean(),
  /**
   * Auto-labeling capability: push classified labels to the mailbox. Optional so
   * a currently-deployed frontend that predates this field can still save (FE and
   * BE deploy independently); the handler preserves the stored value when omitted.
   */
  autoLabel: z.boolean().optional(),
  /**
   * The ids of every custom label this page has loaded or sent. okthx can add a
   * label while the page is open; the server keeps a stored label outside this
   * set, and deletes one inside it that `customLabels` leaves out. Optional so a
   * deployed frontend that predates it can still save, with the old replace.
   */
  seenCustomLabelIds: z.array(z.string()).optional(),
});
export type UpdateEmailLabelConfigReq = z.infer<typeof updateEmailLabelConfigReq>;
export type EmailLabelConfigResp = {
  /** Code-owned labels with the user's color/auto-archive overrides applied; text is read-only. */
  builtIns: EmailLabelCatalogItem[];
  customLabels: EmailLabelCatalogItem[];
  labelPrefix: boolean;
  /** Whether the auto-labeling capability pushes labels to the mailbox. */
  autoLabel: boolean;
};

// The Auto Digest label-selection editor's write shape: the ids of the
// auto-inbox labels (built-in ids or custom-label ids) to summarize in the
// daily digest. The server filters these to ids that exist in the live catalog.
export const updateDigestConfigReq = z.object({
  selectedLabelIds: z.array(z.string()),
});
export type UpdateDigestConfigReq = z.infer<typeof updateDigestConfigReq>;

export type DigestConfigResp = {
  /**
   * The full auto-inbox catalog (built-ins + custom) shown as digest candidates.
   * The daily digest reads the same catalog, so the selector always mirrors what
   * can actually be digested.
   */
  labels: EmailLabelCatalogItem[];
  /** Ids from `labels` currently marked for the daily digest. */
  selectedLabelIds: string[];
};

export const updateRoutineReq = createRoutineReq.partial().extend({
  /**
   * `null` clears the end time, making the routine open-ended again. Omitting
   * the key leaves the stored value untouched — that is what keeps a frontend
   * build predating this field from wiping an end time on save.
   */
  endsAt: z.string().datetime().nullish(),
  /** `null` clears the area, as for `endsAt`. */
  area: userAreaSchema.nullish(),
});

export const routineResp = z.object({
  // For `kind: "user"` this is the ent UUID. For `kind: "system"` it is
  // the stable system slug — the FE uses `/api/routines/system/:id/...`
  // endpoints when kind is system, and `/api/routines/:id` when kind is
  // user.
  id: z.string(),
  kind: z.enum(["user", "system"]),
  // Stable id of the system routine. Set on `kind: "system"` rows only.
  systemRoutineId: z.string().optional(),
  name: z.string(),
  // The effective one-line blurb shown under the name in the routine list. For
  // a system routine this is the user's override layered on the shipped copy;
  // for a user routine it comes off the ent. Optional: routines predating the
  // field have none, and the create forms don't require one.
  description: z.string().optional(),
  // The routine's area id, and the role ids it suits (empty: every role).
  // Strings, not the enums of `routineAreas.ts`: an app build parses this, so
  // an area or role added later must not fail an older build's parse. Optional
  // for the same reason, and because a user routine may have none.
  area: z.string().optional(),
  roles: z.array(z.string()).optional(),
  // For a system row this is the shipped text rendered with the user's
  // settings, and the editor shows it read-only — `amendment` is the part they
  // can change. A user routine owns its prompt outright and has no amendment.
  prompt: z.string(),
  amendment: z.string().optional(),
  triggerConfig: triggerConfigSchema,
  permissions: routinePermissionsSchema,
  enabled: z.boolean(),
  // Absolute instant (ISO 8601, UTC) after which the routine stops firing and
  // turns itself off. Absent means it runs until the user disables it.
  endsAt: z.string().datetime().optional(),
  // Setting definitions declared by a system routine. Present only on system
  // rows that ship a `settings` array — drives the Settings tab in the detail
  // UI. Undefined for user routines.
  settings: z.array(routineSettingDefSchema).optional(),
  // The user's effective setting values (stored override merged over the
  // declared defaults). Present alongside `settings`.
  settingValues: routineSettingValuesSchema.optional(),
  // Always false. Kept on the wire only until the deployed frontend stops
  // reading it; nothing can sit behind a shipped routine's content any more.
  hasSystemUpdate: z.boolean(),
  // Both null for system rows (they aren't ents).
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const listRoutinesResp = z.array(routineResp);

/**
 * Admin variant: per-routine run + completion counts attached to the
 * regular routine payload. Only the admin app (`/api/s/routines/...`)
 * returns this — user-facing routes use `routineResp`.
 */
export const adminRoutineResp = routineResp.extend({
  stats: z.object({
    runs: z.number().int().min(0),
    completed: z.number().int().min(0),
  }),
});
export const adminListRoutinesResp = z.array(adminRoutineResp);

/** Toggle a system routine's enabled state for the current user. */
export const toggleSystemRoutineReq = z.object({
  enabled: z.boolean(),
});

/**
 * Write per-user setting values for a system routine. Partial — only the keys
 * present are updated (instant per-change write-through from the Settings tab).
 * Stored as a preferences override.
 */
export const updateRoutineSettingsReq = z.object({
  settingValues: routineSettingValuesSchema,
});

/**
 * Write per-user permission overrides for a system routine. Stored as a
 * preferences override so the routine still tracks upstream prompt/schedule
 * updates. Sibling of `updateRoutineSettingsReq`.
 */
export const updateRoutinePermissionsReq = z.object({
  permissions: routinePermissionsSchema,
});

/** Write a per-user cron schedule override for a system routine. */
export const updateRoutineScheduleReq = z.object({
  schedule: cronSchedule,
});

/**
 * Write a per-user description override for a system routine. An empty string
 * clears the override and restores the shipped copy, so the field
 * is emptyable rather than `.min(1)`.
 */
export const updateRoutineDescriptionReq = z.object({
  description: z.string().max(ROUTINE_DESCRIPTION_MAX_LENGTH),
});

export const routineThreadResp = z.object({
  id: z.number(),
  title: z.string(),
  // Effective display status (a ThreadStatus value), derived server-side via
  // deriveListThreadStatus — kept as a plain string here to avoid a
  // types/thread.ts → messages.ts → artifacts.ts → routines.ts import cycle.
  status: z.string(),
  createdAt: z.string(),
  sortDate: z.string(),
});

export type CreateRoutineReq = z.infer<typeof createRoutineReq>;
export type UpdateRoutineReq = z.infer<typeof updateRoutineReq>;
export type RoutineResp = z.infer<typeof routineResp>;
export type ListRoutinesResp = z.infer<typeof listRoutinesResp>;
export type AdminRoutineResp = z.infer<typeof adminRoutineResp>;
export type AdminListRoutinesResp = z.infer<typeof adminListRoutinesResp>;
export type ToggleSystemRoutineReq = z.infer<typeof toggleSystemRoutineReq>;
export type UpdateRoutineSettingsReq = z.infer<typeof updateRoutineSettingsReq>;
export type UpdateRoutinePermissionsReq = z.infer<typeof updateRoutinePermissionsReq>;
export type UpdateRoutineScheduleReq = z.infer<typeof updateRoutineScheduleReq>;
export type UpdateRoutineDescriptionReq = z.infer<typeof updateRoutineDescriptionReq>;
export type RoutineThreadResp = z.infer<typeof routineThreadResp>;

/**
 * A routine's state file: the scratch notes the actor carries from one run to
 * the next, in its own words.
 *
 * Exposed for operators to inspect and correct, not as a settings surface. The
 * actor rewrites this file wholesale at the end of every run, so an edit lasts
 * until the next one — and if the routine still derives the same conclusion from
 * its sources, what you removed comes back. The UI says so.
 */
export type RoutineStateResp = {
  path: string;
  content: string;
  /** False for a routine that has not run yet. Not an error. */
  exists: boolean;
};

export const updateRoutineStateReq = z.object({
  content: z.string(),
});
export type UpdateRoutineStateReq = z.infer<typeof updateRoutineStateReq>;
